import { Fix64 } from "../maths/fix64";
import { EventReward, parseReward } from "./events";
import { DailyRoadData, NormalisedData, emptyDaily, emptyNormalised, parseDaily, parseNormalised } from "./dailyRoad";
import { FarmTwistData, NO_FARM_TWIST, parseFarmTwist } from "./supplies";
import { ChallengeData, RecordsData, emptyChallenge, emptyRecords, parseChallenge, parseRecords } from "./challenge";

/**
 * Iron challenges and Endless mode (M21, docs/MODES.md), parsed from the generated bundle's `modes` section
 * (balance/src/modes.json). The waves are built in sim/runModes.ts; unlocks, tries and rewards live in meta/modes.ts.
 */

export interface IronData {
  readonly towersPerLevel: number;
  /** Every raider's health (bosses too) on an Iron run. */
  readonly enemyHpMult: Fix64;
  /** Ticks from a stage block's last spawn to the next block's first. */
  readonly blockGapTicks: number;
  readonly marksBase: number;
  readonly marksPerLevel: number;
  readonly repeatMarksBase: number;
  readonly repeatMarksPerLevel: number;
  readonly repeatWinsPerDay: number;
  /** Tower ids in the order the fallback rule takes them (a level without a `towers` entry, a tower not owned). */
  readonly fallbackOrder: readonly string[];
  /** Per level: the Iron towers' ids (tools/balance/modes.ts --calibrate). */
  readonly towers: ReadonlyMap<number, readonly string[]>;
  /** Per level: its own Iron health multiplier (tools/balance/modes.ts --calibrate); absent = `enemyHpMult`. */
  readonly levelHp: ReadonlyMap<number, Fix64>;
  /** A later (repeat) Iron win also pays these gems and this Forge Essence (gear v2 free routes, docs/GEAR_V2.md). */
  readonly repeatGems: number;
  readonly repeatEssence: number;
  /** Iron star totals: each milestone pays once when the player's Iron stars reach `count` (in count order). */
  readonly starMilestones: readonly Milestone[];
}

/** A free-route milestone (docs/GEAR_V2.md): a threshold and what reaching it pays. */
export interface Milestone { readonly count: number; readonly reward: EventReward }

export interface EndlessGrowthStep { readonly fromWave: number; readonly rate: Fix64 }
export interface EndlessChestBand { readonly fromWave: number; readonly reward: EventReward }

export interface EndlessData {
  readonly unlockAfterLevel: number;
  /** The level whose ground (grid, terrain, biome) an Endless run is played on. */
  readonly mapLevel: number;
  /** In fromWave order; the first starts at wave 1. */
  readonly growth: readonly EndlessGrowthStep[];
  readonly powerCap: Fix64;
  readonly geBase: number;
  readonly gePerWave: number;
  readonly geCap: number;
  readonly typesBase: number;
  readonly typesEveryWaves: number;
  readonly typesMax: number;
  readonly groupCap: number;
  readonly intervalTicks: number;
  readonly groupGapTicks: number;
  readonly firstWaveDelayTicks: number;
  readonly bossEvery: number;
  readonly eliteFromWave: number;
  readonly eliteEvery: number;
  readonly eliteTightenWaves: number;
  readonly eliteEveryMin: number;
  readonly waveCap: number;
  /** In fromWave order. */
  readonly weeklyChest: readonly EndlessChestBand[];
  /** Weekly milestones (gear v2 free routes): each pays once a UTC week when that week's best reaches `count` waves. */
  readonly weeklyMilestones: readonly Milestone[];
}

export interface ModesData {
  readonly iron: IronData;
  readonly endless: EndlessData;
  /** Daily Road (MP-20, data/dailyRoad.ts). */
  readonly daily: DailyRoadData;
  /** The fair-mode profile a `normalised` mode runs with (MP-32). */
  readonly normalised: NormalisedData;
  /** Farm twists on replays of cleared levels (MP-25, data/supplies.ts); none in an older bundle. */
  readonly farm: FarmTwistData;
  /** Challenge codes (MP-28, data/challenge.ts). */
  readonly challenge: ChallengeData;
  /** The Hall of Records (MP-27). */
  readonly records: RecordsData;
}

/** No modes data (an old bundle, a test fixture): Iron and Endless never open. */
export function emptyModes(): ModesData {
  return {
    iron: {
      towersPerLevel: 3, enemyHpMult: Fix64.One, blockGapTicks: 60, marksBase: 0, marksPerLevel: 0, repeatMarksBase: 0, repeatMarksPerLevel: 0,
      repeatWinsPerDay: 0, fallbackOrder: [], towers: new Map(), levelHp: new Map(), repeatGems: 0, repeatEssence: 0, starMilestones: [],
    },
    endless: {
      unlockAfterLevel: Number.MAX_SAFE_INTEGER, mapLevel: 1, growth: [{ fromWave: 1, rate: Fix64.One }], powerCap: Fix64.One,
      geBase: 1, gePerWave: 0, geCap: 1, typesBase: 1, typesEveryWaves: 1, typesMax: 1, groupCap: 1, intervalTicks: 10, groupGapTicks: 10,
      firstWaveDelayTicks: 0, bossEvery: 0, eliteFromWave: Number.MAX_SAFE_INTEGER, eliteEvery: 1, eliteTightenWaves: 1, eliteEveryMin: 1, waveCap: 1, weeklyChest: [], weeklyMilestones: [],
    },
    daily: emptyDaily(),
    normalised: emptyNormalised(),
    farm: NO_FARM_TWIST,
    challenge: emptyChallenge(),
    records: emptyRecords(),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json, where: string, min = 0): number {
  if (typeof v === "number" && Number.isInteger(v) && v >= min) return v;
  throw new TypeError(`modes.${where}: expected an integer >= ${min}, got ${JSON.stringify(v)}`);
}
function fix(v: Json, where: string): Fix64 {
  const f = Fix64.parse(String(v));
  if (f.raw <= 0n) throw new RangeError(`modes.${where}: must be positive`);
  return f;
}
function req(o: Json, key: string, where: string): Json {
  if (o === null || typeof o !== "object" || !(key in o)) throw new Error(`modes.${where}: missing '${key}'`);
  return o[key];
}

/** Milestones in rising `key` order (absent: none). */
function parseMilestones(list: Json, key: string, where: string): Milestone[] {
  if (list === undefined) return [];
  if (!Array.isArray(list)) throw new Error(`modes.${where}: a list`);
  const out = list.map((m, i) => ({ count: int(req(m, key, `${where}[${i}]`), `${where}[${i}].${key}`, 1), reward: parseReward(req(m, "reward", `${where}[${i}]`)) }));
  if (out.some((m, i) => i > 0 && m.count <= out[i - 1].count)) throw new Error(`modes.${where}: thresholds rising`);
  return out;
}
const optional = (o: Json, key: string): Json => (o !== null && typeof o === "object" && key in o ? o[key] : undefined);

function parseIron(i: Json): IronData {
  const r = (k: string) => req(i, k, "iron");
  const towers = new Map<number, string[]>();
  for (const [k, v] of Object.entries(r("towers") as Record<string, Json>)) {
    const level = Number(k);
    if (!Number.isInteger(level) || level < 1 || !Array.isArray(v) || !v.every((s) => typeof s === "string")) throw new Error(`modes.iron.towers.${k}: a level -> tower ids`);
    towers.set(level, v as string[]);
  }
  const levelHp = new Map<number, Fix64>();
  for (const [k, v] of Object.entries((i !== null && typeof i === "object" && "levelHp" in i ? i.levelHp : {}) as Record<string, Json>)) {
    const level = Number(k);
    if (!Number.isInteger(level) || level < 1) throw new Error(`modes.iron.levelHp.${k}: a level -> multiplier`);
    levelHp.set(level, fix(v, `iron.levelHp.${k}`));
  }
  const fallbackOrder = r("fallbackOrder") as Json[];
  if (!Array.isArray(fallbackOrder) || !fallbackOrder.every((s) => typeof s === "string")) throw new Error("modes.iron.fallbackOrder: tower ids");
  return {
    towersPerLevel: int(r("towersPerLevel"), "iron.towersPerLevel", 1), enemyHpMult: fix(r("enemyHpMult"), "iron.enemyHpMult"),
    blockGapTicks: int(r("blockGapTicks"), "iron.blockGapTicks"), marksBase: int(r("marksBase"), "iron.marksBase"), marksPerLevel: int(r("marksPerLevel"), "iron.marksPerLevel"),
    repeatMarksBase: int(r("repeatMarksBase"), "iron.repeatMarksBase"), repeatMarksPerLevel: int(r("repeatMarksPerLevel"), "iron.repeatMarksPerLevel"),
    repeatWinsPerDay: int(r("repeatWinsPerDay"), "iron.repeatWinsPerDay"), fallbackOrder: fallbackOrder as string[], towers, levelHp,
    repeatGems: int(optional(i, "repeatGems") ?? 0, "iron.repeatGems"), repeatEssence: int(optional(i, "repeatEssence") ?? 0, "iron.repeatEssence"),
    starMilestones: parseMilestones(optional(i, "starMilestones"), "stars", "iron.starMilestones"),
  };
}

function parseEndless(e: Json): EndlessData {
  const r = (k: string) => req(e, k, "endless");
  const n = (k: string, min = 0) => int(r(k), `endless.${k}`, min);
  const growth = (r("growth") as Json[]).map((g, i) => ({ fromWave: int(req(g, "fromWave", `endless.growth[${i}]`), `endless.growth[${i}].fromWave`, 1), rate: fix(req(g, "rate", `endless.growth[${i}]`), `endless.growth[${i}].rate`) }));
  if (growth.length === 0 || growth[0].fromWave !== 1 || growth.some((g, i) => i > 0 && g.fromWave <= growth[i - 1].fromWave)) throw new Error("modes.endless.growth: steps from wave 1, rising");
  const weeklyChest = (r("weeklyChest") as Json[]).map((b, i) => ({ fromWave: int(req(b, "fromWave", `endless.weeklyChest[${i}]`), `endless.weeklyChest[${i}].fromWave`, 1), reward: parseReward(req(b, "reward", `endless.weeklyChest[${i}]`)) }));
  if (weeklyChest.some((b, i) => i > 0 && b.fromWave <= weeklyChest[i - 1].fromWave)) throw new Error("modes.endless.weeklyChest: bands rising");
  return {
    unlockAfterLevel: n("unlockAfterLevel", 1), mapLevel: n("mapLevel", 1), growth, powerCap: fix(r("powerCap"), "endless.powerCap"),
    geBase: n("geBase", 1), gePerWave: n("gePerWave"), geCap: n("geCap", 1), typesBase: n("typesBase", 1), typesEveryWaves: n("typesEveryWaves", 1), typesMax: n("typesMax", 1),
    groupCap: n("groupCap", 1), intervalTicks: n("intervalTicks", 1), groupGapTicks: n("groupGapTicks"), firstWaveDelayTicks: n("firstWaveDelayTicks"),
    bossEvery: n("bossEvery"), eliteFromWave: n("eliteFromWave", 1), eliteEvery: n("eliteEvery", 1), eliteTightenWaves: n("eliteTightenWaves", 1), eliteEveryMin: n("eliteEveryMin", 1),
    waveCap: n("waveCap", 1), weeklyChest, weeklyMilestones: parseMilestones(optional(e, "weeklyMilestones"), "wave", "endless.weeklyMilestones"),
  };
}

/** Reads the `modes` section from the bundle root; a bundle without one gets `emptyModes`. */
export function parseModes(root: Json): ModesData {
  const m = root !== null && typeof root === "object" && "modes" in root ? root.modes : undefined;
  if (m === undefined) return emptyModes();
  return { iron: parseIron(req(m, "iron", "")), endless: parseEndless(req(m, "endless", "")), daily: parseDaily(optional(m, "daily")), normalised: parseNormalised(optional(m, "normalised")),
    farm: parseFarmTwist(optional(m, "farm")), challenge: parseChallenge(optional(m, "challenge")), records: parseRecords(optional(m, "records")) };
}
